import React, { useEffect } from "react";
import useStyles from "./home_page_styles";
import {
  Badge,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
} from "@mui/material";
import AdminAppBar from "../../../components/admin_app_bar/app_bar";
import Button1 from "../../../components/button_1/button_1";
import AdminApiService from "../../../api_service/admin_api_service";
import EditRestaurant from "../../../components/edit_restaurant/edit_restaurant";
import { useStoreActions, useStoreState } from "easy-peasy";
import {
  CardMembership,
  Delete,
  Edit,
  RestoreFromTrash,
  UploadFile,
  Download,
} from "@mui/icons-material";
import { getImageUrl } from "../../../utils/helpers";
import { useHistory } from "react-router-dom";
import commaNumber from "comma-number";
import { format } from "date-fns";
import { showAlert } from "../../../components/message_box/show_alert";

export default function AdminHomePage() {
  const classes = useStyles();
  const adminRestaurants = useStoreState((state) => state.adminRestaurants);
  const setOpenRestaurantEditModal = useStoreActions(
    (actions) => actions.setOpenRestaurantEditModal
  );
  const setRestaurantToEdit = useStoreActions(
    (actions) => actions.setRestaurantToEdit
  );
  const [uploadMenuDialogOpen, setUploadMenuDialogOpen] = React.useState(false);
  const [selectedRestaurantId, setSelectedRestaurantId] = React.useState(null);

  useEffect(() => {
    AdminApiService().getAllRestaurantsCall();
  }, []);

  const handleOpenUploadDialog = (restaurantId) => {
    setSelectedRestaurantId(restaurantId);
    setUploadMenuDialogOpen(true);
  };

  const handleCloseUploadDialog = () => {
    setUploadMenuDialogOpen(false);
    setSelectedRestaurantId(null);
  };

  return (
    <div className={classes.root}>
      <div className={classes.right_bar}>
        <AdminAppBar title={"Restaurants"} />
        <div className={classes.main_row}>
          <Button1
            className={classes.add_btn}
            title={"Add New Restaurant"}
            isSelected
            onClick={() => {
              setRestaurantToEdit(null);
              setOpenRestaurantEditModal(true);
            }}
          />
          <RestaurantsTable
            restaurants={adminRestaurants}
            onUploadMenu={handleOpenUploadDialog}
          />
        </div>
      </div>
      <EditRestaurant />
      <MenuUploadDialog
        open={uploadMenuDialogOpen}
        onClose={handleCloseUploadDialog}
        restaurantId={selectedRestaurantId}
      />
    </div>
  );
}

function RestaurantsTable({ restaurants, onUploadMenu }) {
  return (
    <TableContainer component={Paper}>
      <Table aria-label="collapsible table">
        <TableHead>
          <TableRow>
            <TableCell align="center">Logo</TableCell>
            <TableCell align="left" style={{ minWidth: 150 }}>
              Name
            </TableCell>
            <TableCell align="left">Code</TableCell>
            <TableCell align="left" style={{ minWidth: 200 }}>
              Address
            </TableCell>
            <TableCell align="left" style={{ minWidth: 150 }}>
              Subscription Fee
            </TableCell>
            <TableCell align="left" style={{ minWidth: 150 }}>
              Subscribed Until
            </TableCell>
            <TableCell align="center" style={{ minWidth: 160 }}>
              Subscription Status
            </TableCell>
            <TableCell align="center" style={{ minWidth: 170 }}>
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {restaurants.map((restaurant) => (
            <Row
              key={restaurant.id}
              restaurant={restaurant}
              onUploadMenu={onUploadMenu}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function Row({ restaurant, onUploadMenu }) {
  const history = useHistory();
  const setOpenRestaurantEditModal = useStoreActions(
    (actions) => actions.setOpenRestaurantEditModal
  );
  const showEditRestaurant = async () => {
    await AdminApiService().getSpecificRestaurantCall(restaurant.id);
    setOpenRestaurantEditModal(true);
  };

  const toggleStatus = async () => {
    await AdminApiService().updateRestaurantStatusCall(
      {
        status: !restaurant.is_active,
      },
      restaurant.id
    );
    AdminApiService().getAllRestaurantsCall();
  };

  return (
    <React.Fragment>
      <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
        <TableCell align="center">
          {restaurant.logo ? (
            <img src={getImageUrl(restaurant.logo)} alt={"logo"} height={50} />
          ) : (
            <img src="/images/sample_logo_rest.png" alt={"logo"} height={50} />
          )}
        </TableCell>
        <TableCell align="left">{restaurant.name}</TableCell>
        <TableCell align="left">{restaurant.code}</TableCell>
        <TableCell align="left">{restaurant.address}</TableCell>
        <TableCell align="left">
          {commaNumber(Number(restaurant.subscription_fee).toFixed(2))}
        </TableCell>
        <TableCell align="left">
          {restaurant.subscribed_until
            ? format(new Date(restaurant.subscribed_until), "do MMM yyyy")
            : "--"}
        </TableCell>
        <TableCell
          align="center"
          style={{
            fontWeight: "bold",
            color:
              restaurant.subscription_status === "ACTIVE"
                ? "green"
                : restaurant.subscription_status === "EXPIRE_SOON"
                ? "orange"
                : restaurant.subscription_status === "EXPIRED"
                ? "red"
                : "gray",
          }}
        >
          {restaurant.subscription_status}
        </TableCell>
        <TableCell align="center">
          {restaurant.is_active && (
            <>
              <Tooltip title="Edit or View Restaurant">
                <IconButton onClick={showEditRestaurant}>
                  <Edit color="primary" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Upload Menu">
                <IconButton onClick={() => onUploadMenu(restaurant.id)}>
                  <UploadFile color="secondary" />
                </IconButton>
              </Tooltip>
            </>
          )}
          {restaurant.is_active ? (
            <Tooltip title="Deactivate Restaurant">
              <IconButton onClick={toggleStatus}>
                <Delete color="error" />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Activate Restaurant">
              <IconButton onClick={toggleStatus}>
                <RestoreFromTrash color="success" />
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title="Go to subscriptions">
            <Badge
              badgeContent={restaurant.has_pending_subscriptions ? 1 : null}
              color="error"
            >
              <IconButton
                onClick={() => {
                  history.push(`/admin/subscriptions/${restaurant.id}`);
                }}
              >
                <CardMembership color="success" />
              </IconButton>
            </Badge>
          </Tooltip>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

function MenuUploadDialog({ open, onClose, restaurantId }) {
  const [selectedFile, setSelectedFile] = React.useState(null);
  const [isDragging, setIsDragging] = React.useState(false);

  const downloadSampleTemplate = () => {
    const sampleData = [
      [
        "category_name",
        "category_description",
        "item_name",
        "item_description",
        "unit_price",
      ],
    ];

    let csvContent = "";
    sampleData.forEach((row) => {
      csvContent += row.map((cell) => `"${cell}"`).join(",") + "\n";
    });

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "menu_template.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const validTypes = [
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ];
      if (validTypes.includes(file.type)) {
        setSelectedFile(file);
      } else {
        showAlert({
          head: "Invalid File Type",
          body: "Please select a valid Excel file (.xls or .xlsx)",
        });
      }
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files[0];
    if (file) {
      const validTypes = [
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ];
      if (validTypes.includes(file.type)) {
        setSelectedFile(file);
      } else {
        showAlert({
          head: "Invalid File Type",
          body: "Please select a valid Excel file (.xls or .xlsx)",
        });
      }
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      showAlert({
        head: "No File Selected",
        body: "Please select an Excel file to upload",
      });
      return;
    }

    const formData = new FormData();
    formData.append("excel", selectedFile);

    const response = await AdminApiService().importMenuCall(
      restaurantId,
      formData
    );
    setSelectedFile(null);
    onClose();
    if (response.success) {
      showAlert({
        head: "Success",
        body: "Menu imported successfully!",
      });
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Upload Restaurant Menu</DialogTitle>
      <DialogContent>
        <Box sx={{ my: 2 }}>
          <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-end" }}>
            <Button
              startIcon={<Download />}
              onClick={downloadSampleTemplate}
              variant="outlined"
              size="small"
            >
              Download Sample Template
            </Button>
          </Box>
          <Box
            sx={{
              border: isDragging ? "2px dashed #1976d2" : "2px dashed #ccc",
              borderRadius: 2,
              p: 4,
              textAlign: "center",
              backgroundColor: isDragging ? "#f0f8ff" : "#fafafa",
              cursor: "pointer",
              transition: "all 0.3s",
              "&:hover": {
                borderColor: "#1976d2",
                backgroundColor: "#f0f8ff",
              },
            }}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => document.getElementById("file-input").click()}
          >
            <UploadFile sx={{ fontSize: 48, color: "#1976d2", mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {selectedFile
                ? selectedFile.name
                : "Drag and drop Excel file here"}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              or click to browse
            </Typography>
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              Supported formats: .xls, .xlsx
            </Typography>
          </Box>
          <input
            id="file-input"
            type="file"
            accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={handleFileChange}
            style={{ display: "none" }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          color="primary"
          disabled={!selectedFile}
        >
          Upload
        </Button>
      </DialogActions>
    </Dialog>
  );
}
